/**
 * AudioUploadNode (Canvas V2)
 *
 * Upload a local audio file. Supports file picker and drag-and-drop.
 * 50MB limit, backend API upload, waveform visualization (native AudioContext),
 * play/pause toggle, mm:ss / mm:ss time display.
 * FloatingNodeLabel (editable) above the node, hidden output port.
 *
 * Uses a custom card wrapper matching ImageUploadNode's clean,
 * media-focused card style with boxShadow for state indication.
 * No BaseNode header bar — just the audio content.
 *
 * 5 visual states:
 *   1. Empty — clickable label, microphone icon + "Upload Audio"
 *   2. Uploading — waveform area + overlay spinner
 *   3. Error — error icon + message + retry hint
 *   4. Success — waveform + playback controls always visible
 *   5. Hover — overlay buttons (download, replace)
 *
 * Output: out_audio (audio)
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { NodeProps, Position } from 'reactflow'
import { useNodeActions, FloatingNodeLabel } from '../shared'
import { TypedHandle } from '../../TypedHandle'
import { useCanvasOptional } from '../../CanvasContext'
import { useFileUpload } from '../shared/useFileUpload'
import { AudioWaveformVisualization } from '../shared/AudioWaveformVisualization'
import { api } from '../../../../api/client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'

export interface AudioUploadNodeData {
  out_audio?: string
  in_file_name?: string
  fileSize?: number
  duration?: number
  status?: 'idle' | 'running' | 'completed' | 'error'
  error?: string
  label?: string
  /** File object passed on node creation for auto-upload */
  initialFile?: File
  /** Eagerly-created blob URL for instant preview */
  _initialPreviewUrl?: string
}

const NODE_WIDTH = 315

const AudioUploadNode = memo(({ id, data, selected }: NodeProps<AudioUploadNodeData>) => {
  const {
    out_audio,
    in_file_name,
    status = 'idle',
    error,
  } = data

  const canvas = useCanvasOptional()
  const projectId = useProjectId()

  // Track whether the label was manually set by the user
  const labelManuallySet = useRef(false)

  // Local state for immediate label display (Issue 2: frontend-origin YDoc
  // writes skip React state rebuild, so we need local state for instant feedback)
  const [derivedLabel, setDerivedLabel] = useState<string>('')

  const updateNodeData = useCallback(
    (updates: Record<string, unknown>) => {
      // Auto-set label from uploaded filename (if not manually renamed)
      const merged = { ...updates }
      if (updates.in_file_name && typeof updates.in_file_name === 'string' && !labelManuallySet.current) {
        const nameWithoutExt = (updates.in_file_name as string).replace(/\.[^.]+$/, '')
        merged.label = nameWithoutExt
        setDerivedLabel(nameWithoutExt)
      }
      canvas?.updateNodeData(id, merged)
    },
    [id, canvas]
  )

  const {
    previewUrl,
    isUploading,
    handleFileChange,
    dragHandlers,
    isDragOver,
    retry,
    multiFileWarning,
    uploadFile,
  } = useFileUpload({
    acceptMime: 'audio',
    updateNodeData,
    persistedValue: out_audio,
    initialPreviewUrl: data._initialPreviewUrl,
  })

  // Auto-upload initialFile when canvas context is ready
  const initialFileProcessed = useRef(false)
  useEffect(() => {
    if (data.initialFile && canvas && !initialFileProcessed.current) {
      initialFileProcessed.current = true
      uploadFile(data.initialFile)
    }
  }, [data.initialFile, canvas, uploadFile])

  // Clear initialFile from node data after processing
  const initialFileCleared = useRef(false)
  useEffect(() => {
    if (data.initialFile && !initialFileCleared.current) {
      initialFileCleared.current = true
      setTimeout(() => {
        canvas?.updateNodeData(id, { initialFile: undefined })
      }, 100)
    }
  }, [data.initialFile, id, canvas])

  const { handleDownload } = useNodeActions({ nodeId: id })

  const persistedUrl = out_audio ? api.getAssetUrl(projectId, out_audio) : undefined
  const displayUrl = previewUrl || persistedUrl

  const onDownload = useCallback(() => {
    if (displayUrl) {
      handleDownload(displayUrl, in_file_name || 'uploaded-audio.mp3')
    }
  }, [displayUrl, in_file_name, handleDownload])

  const handleRename = useCallback(
    (newName: string) => {
      labelManuallySet.current = true
      setDerivedLabel(newName)
      canvas?.updateNodeData(id, { label: newName })
    },
    [id, canvas]
  )

  const inputId = `file-upload-${id}`
  const isError = status === 'error'
  const hasError = isError && !!error

  return (
    <div className="relative">
      {/* Floating editable label above the card */}
      <FloatingNodeLabel
        name={derivedLabel || data.label || ''}
        fallback="Untitled"
        onRename={handleRename}
      />

      {/* Card wrapper — matches ImageUploadNode's boxShadow pattern */}
      <div
        className="rounded-lg overflow-hidden"
        style={{
          width: NODE_WIDTH,
          boxShadow: selected
            ? '0 0 0 2px rgba(255,255,255,0.7)'
            : hasError
              ? '0 0 0 1px rgba(239,68,68,0.5)'
              : '0 2px 8px rgba(0,0,0,0.3)',
        }}
        {...dragHandlers}
      >
        {/* Hidden file input */}
        <input
          type="file"
          accept="audio/*"
          onChange={handleFileChange}
          className="hidden"
          id={inputId}
          disabled={isUploading}
        />

        {/* Main content area — 5 visual states */}
        {isUploading && displayUrl ? (
          /* -- State 2: Uploading — waveform area + overlay spinner -- */
          <div className="relative bg-canvas p-3">
            <AudioWaveformVisualization
              url={displayUrl}
              height={80}
            />

            {/* Overlay spinner */}
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center rounded-lg">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span className="text-xxs text-white/80 mt-2">Uploading audio...</span>
            </div>
          </div>
        ) : hasError ? (
          /* -- State 3: Error — icon + message + retry -- */
          <div
            className="w-full bg-canvas flex flex-col items-center justify-center px-6 cursor-pointer"
            style={{ aspectRatio: '16/9' }}
            onClick={retry}
          >
            <svg
              className="w-8 h-8 text-red-400/60"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
            <span
              className="text-xxs text-red-400/80 mt-1.5 text-center line-clamp-2 cursor-help"
              title={error}
            >
              Failed to upload
            </span>
            <span className="text-xxs text-foreground/20 mt-1">Click to retry</span>
          </div>
        ) : displayUrl ? (
          /* -- State 4 + 5: Success — waveform + playback always visible, hover shows overlay buttons -- */
          <div className="relative group bg-canvas p-3 nodrag">
            <AudioWaveformVisualization
              url={displayUrl}
              height={80}
            />

            {/* -- State 5: Hover overlay buttons — download, replace -- */}
            <div className="absolute top-2 right-2 flex gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
              {/* Download */}
              <button
                onClick={(e) => { e.stopPropagation(); onDownload() }}
                className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                title="Download"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
              {/* Replace — label triggers file picker */}
              <label
                htmlFor={inputId}
                className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors cursor-pointer nodrag"
                title="Replace"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </label>
            </div>
          </div>
        ) : (
          /* -- State 1: Empty — clickable upload area -- */
          <label
            htmlFor={inputId}
            className={`w-full bg-canvas flex flex-col items-center justify-center cursor-pointer transition-colors ${
              isDragOver
                ? 'bg-primary/5 ring-2 ring-primary/40 ring-inset'
                : ''
            }`}
            style={{ aspectRatio: '16/9' }}
          >
            {isDragOver ? (
              <span className="text-xs text-primary">Drop to upload</span>
            ) : (
              <>
                <svg
                  className="w-8 h-8 text-foreground/15"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                  />
                </svg>
                <span className="text-xxs text-foreground/30 mt-1">Upload Audio</span>
              </>
            )}
          </label>
        )}

        {/* Multi-file drop warning */}
        {multiFileWarning && (
          <div className="px-2 py-1.5 bg-amber-500/10 border-t border-amber-500/30">
            <span className="text-xxs text-amber-400">{multiFileWarning}</span>
          </div>
        )}
      </div>

      {/* Output handle: out_audio — visually hidden, kept in DOM for edges */}
      <TypedHandle
        type="source"
        position={Position.Right}
        id="out_audio"
        portType="audio"
        style={{ top: '50%', width: 0, height: 0, border: 'none', background: 'transparent', minWidth: 0, minHeight: 0 }}
      />
    </div>
  )
})

AudioUploadNode.displayName = 'AudioUploadNode'
export default AudioUploadNode
